import { createTheme } from "@mui/material/styles"

/**
 * Retroove Theme - Warm, family-friendly color palette
 *
 * Design principles:
 * - Warm colors (terracotta, orange, beige) for a welcoming feel
 * - High contrast for accessibility
 * - Readable typography for both mobile and large screens
 */

const theme = createTheme({
  palette: {
    primary: {
      main: "#E07A5F", // Terracotta/Corail
      light: "#F2A385",
      dark: "#C75D42",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F4A261", // Orange doré
      light: "#FFB74D",
      dark: "#E08C3B",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#FFF8F0", // Crème/Beige clair
      paper: "#FFFFFF",
    },
    text: {
      primary: "#4A3728", // Marron chaud
      secondary: "#6B5344",
    },
    success: {
      main: "#81B29A", // Vert sauge
      light: "#A8D5BA",
      dark: "#5D8A72",
    },
    error: {
      main: "#E07A5F", // Same as primary for consistency
      light: "#F2A385",
      dark: "#C75D42",
    },
    warning: {
      main: "#F4A261", // Orange doré
      light: "#FFB74D",
      dark: "#E08C3B",
    },
  },
  typography: {
    fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
    // Larger sizes for readability on Board (TV) view
    h1: {
      fontWeight: 700,
      fontSize: "3rem",
      "@media (min-width:768px)": {
        fontSize: "4.5rem",
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.5rem",
      "@media (min-width:768px)": {
        fontSize: "3.5rem",
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: "2rem",
      "@media (min-width:768px)": {
        fontSize: "2.5rem",
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      "@media (min-width:768px)": {
        fontSize: "2rem",
      },
    },
    body1: {
      fontSize: "1rem",
      "@media (min-width:768px)": {
        fontSize: "1.25rem",
      },
    },
    button: {
      fontWeight: 600,
      textTransform: "none", // More friendly, less shouty
    },
  },
  shape: {
    borderRadius: 12, // Rounded corners for friendly feel
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24, // Pill-shaped buttons
          padding: "12px 24px",
          fontSize: "1.1rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #E07A5F 0%, #F4A261 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #C75D42 0%, #E08C3B 100%)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "#FFFCF9",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E07A5F",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E07A5F",
              borderWidth: 2,
            },
          },
          "& .MuiInputLabel-root": {
            color: "#6B5344",
            "&.Mui-focused": {
              color: "#E07A5F",
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            backgroundColor: "#FFFCF9",
          },
        },
        paper: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          marginTop: 4,
        },
        option: {
          borderRadius: 8,
          margin: "2px 8px",
          "&:hover": {
            backgroundColor: "#FFF0E6",
          },
          // Keyboard parity with :hover (audit L4 — WCAG 2.1.3 AAA).
          // MUI applies `.Mui-focused` on the option under arrow-key
          // navigation; mirror the hover style so SR / keyboard users
          // see the same highlight as mouse users.
          "&.Mui-focused, &:focus, &:focus-visible": {
            backgroundColor: "#FFF0E6",
          },
          '&[aria-selected="true"]': {
            backgroundColor: "#FFE4D6",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        },
      },
    },
  },
})

export default theme
